import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { Loader2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Support() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { hasAccess, isLoading: roleLoading } = useUserRole();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasPaidAccess = hasAccess(['admin', 'paid', 'student']);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // In a real implementation, this would send an email or create a support ticket
      // For now, we'll just show a success message
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success("Support request submitted successfully! We'll respond within 24 hours.");
      setSubject("");
      setMessage("");
    } catch (error) {
      console.error('Error submitting support request:', error);
      toast.error("Failed to submit support request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading || roleLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </PageLayout>
    );
  }

  if (!user) {
    return (
      <PageLayout>
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Lock className="h-8 w-8 text-accent" />
            </div>
            <CardTitle>Sign In Required</CardTitle>
            <CardDescription>
              Please sign in to access support
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/auth")}>
              Sign In
            </Button>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  if (!hasPaidAccess) {
    return (
      <PageLayout>
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Lock className="h-8 w-8 text-accent" />
            </div>
            <CardTitle>Paid Members Only</CardTitle>
            <CardDescription>
              Support is available exclusively to Student & Educator and Paid members to prevent spam and ensure quality assistance.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upgrade to a paid membership to access priority support from our team.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-4 border-2">
                <h3 className="font-semibold mb-2">Student & Educator</h3>
                <p className="text-2xl font-bold mb-2">$44/year</p>
                <p className="text-sm text-muted-foreground">Includes support access + lesson plans</p>
              </Card>
              <Card className="p-4 border-2 border-accent">
                <h3 className="font-semibold mb-2">Paid Member</h3>
                <p className="text-2xl font-bold mb-2">$88/year</p>
                <p className="text-sm text-muted-foreground">Priority support</p>
              </Card>
            </div>
            <Button onClick={() => navigate("/membership")}>
              View Membership Options
            </Button>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <Mail className="h-8 w-8 text-accent" />
          </div>
          <CardTitle>Contact Support</CardTitle>
          <CardDescription>
            Submit your question or issue and we'll respond within 24 hours
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="subject" className="text-sm font-medium">
                Subject
              </label>
              <Input
                id="subject"
                placeholder="Brief description of your issue"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="message" className="text-sm font-medium">
                Message
              </label>
              <Textarea
                id="message"
                placeholder="Please provide detailed information about your issue or question..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSubmitting}
                rows={8}
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/membership")}
              >
                Back to Membership
              </Button>
            </div>
          </form>

          <div className="mt-6 p-4 bg-muted rounded-lg">
            <h3 className="font-semibold mb-2">Before You Submit</h3>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-4">
              <li>Check our Terms & Conditions and Privacy Policy for common questions</li>
              <li>Include your account email if different from the registered one</li>
              <li>For refund requests, mention your SuperCalc usage if applicable</li>
              <li>Response time: typically within 24 hours on business days</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </PageLayout>
  );
}
